/**
 * Bitget P2P Adapter — normalizes all Bitget API calls to the domain model.
 *
 * Correct endpoint: POST https://www.bitget.com/v1/p2p/pub/adv/queryAdvList
 * Response format: { code: "00000", data: { dataList: Ad[], hasNextPage, totalCount } }
 *
 * Bitget Ad fields → Domain:
 *   adNo → advNo
 *   priceValue / price → price (number → string)
 *   amount → surplusAmount (string → number)
 *   coinCode → asset
 *   adType (2) → tradeType ("SELL")
 *   nickName → advertiser.nickName
 *   encryptUserId → advertiser.userNo
 *   certifiedMerchant → advertiser.merchantLevel (1 or 2)
 *   goodEvaluationRate → advertiser.monthFinishRate (parse "99" → 0.99)
 *   turnoverNum → advertiser.monthOrderCount
 *   minAmount / maxAmount → min/maxSingleTransAmount
 *   paymethodInfo[] → tradeMethods[]
 *   payDuration → payTimeLimit
 */

import axios from "axios";
import { ExchangeAdapter } from "./types";
import { P2PAd, SearchParams, SearchResult, P2PAdDetail } from "./domain";

// ─── Bitget P2P API endpoint ────────────────────────────────────────
const BITGET_P2P_HOST = "www.bitget.com";
const BITGET_P2P_PATH = "/v1/p2p/pub/adv/queryAdvList";

// CORS proxies that work with Bitget
const BITGET_CORS_PROXIES = [
  "https://freehost-2.lehongthai.vip/cors-anywhere.php",
  "https://host-1.lehongthai.vip/cors-anywhere.php",
];

// USDT coin code for Bitget P2P
const BITGET_USDT_COIN_CODE = "USDT";

// ─── Helper: pick random host ───────────────────────────────────────
function randomHost<T>(hosts: T[]): T {
  return hosts[Math.floor(Math.random() * hosts.length)];
}

// ─── Request queue to avoid 429 rate limits ─────────────────────────
const bitgetQueue: Array<{ task: () => Promise<void> }> = [];
let bitgetQueueProcessing = false;
const BITGET_QUEUE_INTERVAL = 500; // ms between requests

function processQueue() {
  if (bitgetQueueProcessing || bitgetQueue.length === 0) return;
  bitgetQueueProcessing = true;
  const run = () => {
    if (bitgetQueue.length === 0) {
      bitgetQueueProcessing = false;
      return;
    }
    const item = bitgetQueue.shift()!;
    item.task().finally(() => {
      setTimeout(run, BITGET_QUEUE_INTERVAL);
    });
  };
  run();
}

function enqueue<T>(factory: () => Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    bitgetQueue.push({
      task: () => factory().then(resolve).catch(reject),
    });
    processQueue();
  });
}

// ─── Helper: detect mobile from User-Agent ──────────────────────────
function isMobileFromUA(): boolean {
  return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

// ─── Parse Bitget rating string like "99" → number (0.99) ───────────
function parseRating(raw: string): number {
  if (!raw) return 0;
  const num = parseFloat(raw);
  if (isNaN(num)) return 0;
  return num / 100;
}

// ─── Normalize a Bitget Ad to P2PAd ─────────────────────────────────
function normalizeAd(ad: any): P2PAd {
  const paymethods = Array.isArray(ad.paymethodInfo) ? ad.paymethodInfo : [];

  return {
    advNo: String(ad.adNo ?? ""),
    tradeType: ad.adType === 2 ? "SELL" : "BUY",
    asset: (ad.coinCode ?? "USDT").toUpperCase(),
    price: String(ad.priceValue ?? ad.price ?? 0),
    surplusAmount: parseFloat(String(ad.amount ?? 0)),
    tradableQuantity: String(ad.amount ?? ""),
    maxSingleTransAmount: parseFloat(String(ad.maxAmount ?? 0)),
    minSingleTransAmount: parseFloat(String(ad.minAmount ?? 0)),
    dynamicMaxSingleTransAmount: parseFloat(String(ad.maxAmount ?? 0)),
    commissionRate: 0,
    payTimeLimit: ad.payDuration ?? 15,
    tradeMethods: paymethods.map((pm: any) => ({
      payId: String(pm.paymethodId ?? null),
      payMethodId: String(pm.paymethodId ?? ""),
      payType: String(pm.paymethodId ?? ""),
      payAccount: null,
      payBank: null,
      paySubBank: null,
      identifier: String(pm.paymethodId ?? ""),
      tradeMethodName: pm.paymethodName ?? "",
      tradeMethodShortName: pm.paymethodName ?? "",
      tradeMethodBgColor: pm.colorValue ?? "#FF9300",
      iconUrl: pm.iconUrl ?? "",
    })),
    advertiser: {
      userNo: String(ad.encryptUserId ?? ""),
      nickName: ad.nickName ?? "",
      userType: ad.certifiedMerchant ? "merchant" : "user",
      monthFinishRate: parseRating(ad.goodEvaluationRate ?? "0"),
      monthOrderCount: parseInt(String(ad.turnoverNum ?? "0"), 10),
      positiveRate: parseRating(ad.goodEvaluationRate ?? "0"),
      activeTimeInSecond: ad.showOnline ? 0 : 9999,
      vipLevel: 0,
      merchantLevel: ad.certifiedMerchant ?? 0,
    },
  };
}

// ─── Fake ads detection (Bitget-specific heuristics) ────────────────
function isFakeAds(ad: P2PAd): boolean {
  const dynamicMaxTrade = ad.dynamicMaxSingleTransAmount;
  const percentSuccess = (ad.advertiser.monthFinishRate ?? 0) * 100;
  const minTrade = ad.minSingleTransAmount;

  if (dynamicMaxTrade <= 5_000_000) return true;
  if (percentSuccess < 90) return true;
  if (minTrade >= 50_000_000 && percentSuccess < 97 && dynamicMaxTrade >= 1_000_000_000) return true;
  if (dynamicMaxTrade / minTrade < 3 && (dynamicMaxTrade < 8_000_000 || percentSuccess < 98)) return true;

  // Bitget — tight range + high price is a classic fake pattern
  if (minTrade < 200_000 && dynamicMaxTrade > 490_000_000 && Number(ad.price) > 27_500) return true;

  return false;
}

// ─── Bitget price signals (whale detection) ─────────────────────────
function getPriceSignals(ad: P2PAd): Array<{ emoji: string; label: string; priceClass: string }> {
  const signals: Array<{ emoji: string; label: string; priceClass: string }> = [];
  const orderCount = ad.advertiser.monthOrderCount;
  const successRate = ad.advertiser.monthFinishRate ?? 0;

  // Whale detection based on order count + success rate
  if (orderCount >= 50000 && successRate >= 0.99) {
    if (orderCount >= 100000) {
      signals.push({ emoji: "🐋", label: "Cá ông", priceClass: "text-yellow-300" });
    } else if (orderCount >= 80000) {
      signals.push({ emoji: "🐳", label: "Cá mập", priceClass: "text-cyan-300" });
    } else if (orderCount >= 50000) {
      signals.push({ emoji: "🐬", label: "Cá heo", priceClass: "text-blue-300" });
    }
  }

  // Dragon (rồng) — high volume + high rate
  if (orderCount >= 30000 && successRate >= 0.98) {
    signals.push({ emoji: "🐲", label: "Rồng", priceClass: "text-red-400" });
  }

  return signals;
}

// ══════════════════════════════════════════════════════════════════════
//  Bitget Adapter Implementation
// ══════════════════════════════════════════════════════════════════════
export const bitgetAdapter: ExchangeAdapter = {
  id: "bitget",
  displayName: "Bitget P2P",
  title: "Bitget P2P",
  favicon: "/bitget-favicon.svg",

  // ─── searchAds ───────────────────────────────────────────────────
  async searchAds(params: SearchParams, isMobile?: boolean): Promise<SearchResult> {
    try {
      const mobile = isMobile ?? isMobileFromUA();

      // UI sends tradeType: 0 = SELL, 1 = BUY
      // Bitget API: side=0 → adType=1 (BUY ads), side=1 → adType=2 (SELL ads)
      const side = params.tradeType === 0 ? 1 : 0;

      const body = {
        side,
        pageNo: params.page || 1,
        pageSize: params.rows || 20,
        coinCode: params.asset || BITGET_USDT_COIN_CODE,
        fiatCode: "VND",
        price: params.transAmount || "",
        orderBy: 1,
        adAreaId: 0,
        attentionMerchantFlag: false,
        rookieFriendlyFlag: false,
        allowPlaceOrderFlag: "1",
        languageType: 0,
      };

      const targetUrl = `https://${BITGET_P2P_HOST}${BITGET_P2P_PATH}`;

      const config = mobile
        ? {
            method: "post" as const,
            url: `${randomHost(BITGET_CORS_PROXIES)}?url=${encodeURIComponent(targetUrl)}`,
            headers: {
              "Content-Type": "application/json;charset=UTF-8",
              Accept: "application/json, text/plain, */*",
              "Accept-Language": "en-GB,en-US;q=0.9,en;q=0.8",
            },
            data: body,
          }
        : {
            method: "post" as const,
            url: targetUrl,
            headers: {
              "Content-Type": "application/json;charset=UTF-8",
              Accept: "application/json, text/plain, */*",
              "Accept-Language": "en-GB,en-US;q=0.9,en;q=0.8",
            },
            data: body,
          };

      const res = await enqueue(() =>
        axios(config)
          .then((r) => r.data)
          .catch((err) => err?.response?.data || null)
      );

      // Bitget response: { code: "00000", data: { dataList: Ad[], hasNextPage, totalCount } }
      let adsList: any[] = [];
      if (res?.code === "00000" && res?.data?.dataList) {
        adsList = res.data.dataList;
      }

      const normalized = adsList.map((item: any) => normalizeAd(item));

      return { data: normalized };
    } catch (error) {
      console.error("[Bitget] searchAds error:", error);
      return { data: [] };
    }
  },

  // ─── getAdDetail ─────────────────────────────────────────────────
  async getAdDetail(advNo: string, isMobile?: boolean): Promise<P2PAdDetail | null> {
    try {
      const mobile = isMobile ?? isMobileFromUA();

      const body = {
        side: 1,  // fetch SELL ads (merchant perspective)
        pageNo: 1,
        pageSize: 100,
        coinCode: BITGET_USDT_COIN_CODE,
        fiatCode: "VND",
        price: "",  // all prices
        orderBy: 1,
        adAreaId: 0,
        attentionMerchantFlag: false,
        rookieFriendlyFlag: false,
        allowPlaceOrderFlag: "1",
        languageType: 0,
      };

      const targetUrl = `https://${BITGET_P2P_HOST}${BITGET_P2P_PATH}`;

      const config = mobile
        ? {
            method: "post" as const,
            url: `${randomHost(BITGET_CORS_PROXIES)}?url=${encodeURIComponent(targetUrl)}`,
            headers: {
              "Content-Type": "application/json;charset=UTF-8",
              Accept: "application/json, text/plain, */*",
              "Accept-Language": "en-GB,en-US;q=0.9,en;q=0.8",
            },
            data: body,
          }
        : {
            method: "post" as const,
            url: targetUrl,
            headers: {
              "Content-Type": "application/json;charset=UTF-8",
              Accept: "application/json, text/plain, */*",
              "Accept-Language": "en-GB,en-US;q=0.9,en;q=0.8",
            },
            data: body,
          };

      const res = await enqueue(() =>
        axios(config)
          .then((r) => r.data)
          .catch((err) => err?.response?.data || null)
      );

      if (!res || res.code !== "00000" || !res.data?.dataList) return null;

      const found = res.data.dataList.find((a: any) => String(a.adNo) === advNo);
      if (!found) return null;

      const ad = normalizeAd(found);

      return {
        adv: {
          ...ad,
          initAmount: ad.surplusAmount,
          createTime: parseInt(String(found.createTime), 10),
          advUpdateTime: parseInt(String(found.adEditorTime), 10),
          advStatus: found.state ?? 1,
          priceType: found.priceType ?? 1,
          priceFloatingRatio: String(found.floatValue ?? "0"),
          rateFloatingRatio: String(found.turnoverRate ?? "1.00"),
          currencyRate: null,
          fiatUnit: found.fiatCode ?? "VND",
          fiatScale: found.fiatPrecision ?? 0,
          priceScale: found.coinPrecision ?? 2,
          fiatSymbol: found.fiatSymbol ?? "₫",
          assetLogo: found.iconUrl ?? "",
          minSingleTransQuantity: String(found.minAmount ?? ""),
          maxSingleTransQuantity: String(found.maxAmount ?? ""),
          dynamicMaxSingleTransQuantity: String(found.maxAmount ?? ""),
          allowTradeMerchant: found.allowMerchantPlace ?? 1,
          buyerRegDaysLimit: -1,
          buyerBtcPositionLimit: -1,
          remarks: "",
          abnormalStatusList: [],
          autoReplyMsg: null,
          takerCommissionRate: null,
          minTakerFee: null,
          isSafePayment: false,
          inventoryType: found.customizeState ?? 0,
          offlineReason: "",
          closeReason: "",
          userTradeCountFilterTime: 0,
          userBuyTradeCountMin: 0,
          userBuyTradeCountMax: 0,
          userSellTradeCountMin: 0,
          userSellTradeCountMax: 0,
          userAllTradeCountMin: 0,
          userAllTradeCountMax: 0,
          userTradeCompleteRateFilterTime: null,
          userTradeCompleteCountMin: 0,
          userTradeCompleteRateMin: "",
          userTradeVolumeFilterTime: 0,
          userTradeType: 0,
          userTradeVolumeMin: "",
          userTradeVolumeMax: "",
          userTradeVolumeAsset: null,
          launchCountry: null,
          tradeMethodCommissionRates: [],
        },
        advertiser: {
          ...ad.advertiser,
          orderCount: parseInt(String(found.turnoverNum ?? "0"), 10),
          userStatsRet: {
            registerDays: 0,
            firstOrderDays: 0,
            avgReleaseTimeOfLatest30day: 0,
            avgPayTimeOfLatest30day: 0,
            finishRateLatest30day: ad.advertiser.monthFinishRate,
            completedOrderNumOfLatest30day: 0,
            completedBuyOrderNumOfLatest30day: 0,
            completedSellOrderNumOfLatest30day: 0,
            completedOrderTotalBtcAmountOfLatest30day: 0,
            completedOrderNum: 0,
            completedBuyOrderNum: 0,
            completedSellOrderNum: 0,
            completedBuyOrderTotalBtcAmount: 0,
            completedSellOrderTotalBtcAmount: 0,
            completedOrderTotalBtcAmount: 0,
            counterpartyCount: 0,
          },
          userKycRet: undefined,
          businessStatus: null,
          overComplained: null,
          onlineStatus: found.showOnline ? "online" : "offline",
          registerDays: null,
          firstOrderDays: null,
          chanelInvolved: "",
          userIdentity: ad.advertiser.userType === "merchant" ? "MERCHANT" : "USER",
          userGradeInstanceRet: null,
          proMerchant: ad.advertiser.userType === "merchant",
          badges: [],
          tradeOrderCount: null,
          isBlocked: null,
          alertEnabled: null,
          lastActiveTime: null,
          companyAccountName: "",
          isCompanyAccount: false,
          eqMargin: 0,
          eqMarginUnit: "",
          marginStats: [],
          avatar: null,
          margin: 0,
          marginUnit: "",
          decimalScale: 0,
          advConfirmTime: 0,
          emailVerified: false,
          bindMobile: false,
          kycVerified: false,
          registrationTime: null,
          payMethodCount: ad.tradeMethods.length,
          mobile: null,
          kycType: 0,
          kycStatus: "",
          userGrade: 0,
        },
      };
    } catch {
      return null;
    }
  },

  // ─── isFakeAd ────────────────────────────────────────────────────
  isFakeAd: (ad: P2PAd) => isFakeAds(ad),

  // ─── getVipStyle (Bitget — no special VIP styling) ─────────────
  getVipStyle: () => null,

  // ─── getPriceSignals (Bitget — whale detection) ─────────────────
  getPriceSignals: (ad: P2PAd) => getPriceSignals(ad),

  // ─── getMerchantColorRules (Bitget — certified-based) ───────────
  getMerchantColorRules: (ad: P2PAd) => {
    const level = (ad.advertiser as any).merchantLevel ?? 0;
    return level >= 1 ? "text-cyan-300" : null;
  },

  // ─── isMerchantPerspective (Bitget: merchant perspective) ───────
  isMerchantPerspective: () => true,

  // ─── getLogo (Bitget — merchantLevel-based) ─────────────────────
  getLogo: (ad: P2PAd) => {
    const level = (ad.advertiser as any).merchantLevel ?? 0;
    if (level >= 2) return "👑";  // certified (advanced)
    if (level >= 1) return "⚜️";  // certified (basic)
    return "🐮";                  // normal user
  },

  // ─── Bitget P2P links (no external linking supported) ───────────
  getAdvertiserLink: () => null,
  getLink: () => null,
};
